/** @jsx isolateComponent */

import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import type { Interpolation } from '@emotion/css';

import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';
import { normalizePathname } from '@zapier/url-utils';
import { Animation, Colors } from '@zapier/design-system';

import UniversalNavLink from '@zapier/universal-nav/dist/components/UniversalNavLink';
import UniversalSidebarSubmenu from '@zapier/universal-nav/dist/components/UniversalSidebar/UniversalSidebarSubmenu/UniversalSidebarSubmenu';
import {
  UniversalSidebarLinks,
  skipLinkHref,
} from '@zapier/universal-nav/dist/constants';

import type { UniversalNavLinkType } from '@zapier/universal-nav/dist/constants';

export const UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH = 70;
export const UNIVERSAL_SIDEBAR_WIDTH = 300;

export type Props = {
  /**
   * Label describing the collapse button.
   */
  collapseButtonLabel?: string;
  /**
   * Indicates whether the navigation is collapsed down to icons.
   */
  isCollapsed: boolean;
  /**
   * Whether the sidebar is as wide as it can be.
   * When `true`, will render as wide as it can.
   * When `false`, will constrain to a `max-width`.
   */
  isFullWidth: boolean;
  /**
   * Whether the sidebar has a shadow. Should only be used when
   * the sidebar is floating.
   */
  isShadowed?: boolean;
  /**
   * Array of links to render in the sidebar.
   */
  links: Array<UniversalNavLinkType>;
  /**
   * Label describing where the Zapier logo links to.
   */
  logoAriaLabel: string;
  /**
   * Where the Zapier logo links to.
   */
  logoHref: string;
  /**
   * Optional handler for when anywhere on the nav is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
  /**
   * Optional handler for when the `nav` is hovered.
   */
  onMouseEnter?: (e: ReactMouseEvent) => void;
  /**
   Optional handler for when the `nav` is unhovered.
   */
  onMouseLeave?: (e: ReactMouseEvent) => void;
  /**
   * Renders content above the links. Typically that content is
   * a CTA button, such as to create a Zap.
   */
  renderSubheader?: (props: Props) => ReactNode;
  /**
   * Renders content below the links. Typically that content is
   * usage data or CTA buttons.
   */
  renderFooter: () => ReactNode;
  /**
   * The `href` of the currently selected link within `links`.
   */
  selectedHref: string;
  /**
   * Anchor to the main content area of the page.
   * **The page must contain a node with the `id` in this prop!**
   */
  skipLinkHref: string;
};

const GAP = 20;

const Styles = labelStyles(__filename, {
  root: (props: Props) => [
    {
      position: 'relative',
      // Create grid to stretch content so that it scrolls.
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: Colors.white,
      padding: '0 10px',
      // Necessary to set explicitly so `transition` works.
      width: '100%',
      transition: Animation.transitionValue,
      // Noticed some jankiness with `transition` and `animation`.
      // This appears to improve it at least a bit.
      willChange: 'transform, width',
    },

    !props.isFullWidth &&
      withModifier('constrainedWidth', {
        width: UNIVERSAL_SIDEBAR_WIDTH,
        maxWidth: '100vw',
      }),

    !props.isFullWidth && withModifier('bordered', {
      // Add a border-right w/o the border actually taking
      // up any box model space. border-right reduces the inner
      // width of the content and causes horizontal scrolling
      // which makes this necessary.
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: 1,
        backgroundColor: Colors.grey200,
      }
    }),

    // Sidebar shadow. Using a pseudo element instead of `box-shadow`
    // so the shadow only goes in one direction.
    props.isShadowed &&
      withModifier('shadowed', {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '100%',
          bottom: 0,
          width: 10,
          pointerEvents: 'none',
          background: `linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.10) 0%,
            rgba(0, 0, 0, 0.03) 50%,
            rgba(0, 0, 0, 0.00) 100%
          )`,
        }
      }),

    props.isCollapsed &&
      withModifier('collapsed', {
        width: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
        overflow: 'visible',
      }),
  ],

  header: {
    flex: 'none',
    paddingTop: GAP,
  },

  content: [
    {
      overflow: 'auto',
      paddingTop: GAP,
      paddingBottom: GAP,
      flexBasis: 'auto',
      flexShrink: 1,
      flexGrow: 0,

      // Hide scrollbar, especially on Windows and in Firefox on macOS.
      // Shouldn't be a usability issue since a) scrolling is unlikely
      // to be necessary for most screens, and b) scrolling should still
      // be obvious due to clipped content.
      scrollbarWidth: 'none',
      '::-webkit-scrollbar': {
        display: 'none',
      },
    },
  ],

  links: [
    {
      display: 'grid',
      alignContent: 'start',
      gap: 15,
    },
  ],

  footer: {
    flex: 'none',
    borderTop: `1px solid ${Colors.grey200}`,
    paddingTop: GAP,
    paddingBottom: GAP,
  },
});

// Indicates whether the given `href` should be considered selected
// based on the value of `selectedHref`. The rules of this are somewhat
// specific to `UniversalSidebar`, so be careful if you try to make
// this more generic!
export const isLinkSelected = (href: string, selectedHref: string) => {
  return normalizePathname(href).endsWith(normalizePathname(selectedHref));
};

const Links = (props: Props) => {
  const links = props.links.map((link: UniversalNavLinkType) => {
    const linkOrMenu = link.submenu && !link.href ? (
      <UniversalSidebarSubmenu
        links={link.submenu}
        text={link.text}
      />
    ) : (
      <UniversalNavLink
        href={link.href}
        icon={link.icon}
        isCollapsed={props.isCollapsed}
        isSelected={isLinkSelected(link.href || '', props.selectedHref)}
        text={link.text}
      />
    );
    return (
      <li key={link.text}>
        {linkOrMenu}
      </li>
    );
  });

  return <ul css={Styles.links as Interpolation}>{links}</ul>;
};

/**
 * The universal sidebar that renders when users are logged in and
 * on an app page. Renders the universal navigation, a content footer,
 * and some other small bites.
 */
const UniversalSidebar = (props: Props) => {
  const mouseProps = {
    onClick: props.onClick,
    onMouseEnter: props.onMouseEnter,
    onMouseLeave: props.onMouseLeave,
  };
  const subheaderContent = props.renderSubheader && props.renderSubheader(props);
  const subheader = subheaderContent && (
    <div css={Styles.header as Interpolation}>{subheaderContent}</div>
  );
  const footerContent = props.renderFooter();
  const footer = footerContent && (
    <div css={Styles.footer as Interpolation}>{footerContent}</div>
  );
  return (
    <nav
      css={Styles.root(props) as Interpolation}
      data-testid="universalSidebar"
      {...mouseProps}
    >
      {subheader}
      <div css={Styles.content as Interpolation}>
        <Links {...props} />
      </div>
      {footer}
    </nav>
  );
};

UniversalSidebar.defaultProps = {
  isCollapsed: false,
  isFullWidth: true,
  links: UniversalSidebarLinks,
  logoHref: '/',
  logoAriaLabel: 'Go to homepage',
  renderFooter: () => null,
  selectedHref: '',
  skipLinkHref,
};

export default UniversalSidebar;
